/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */
import Button from '@alltrails/shared/denali/components/Button';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import Close from '@alltrails/shared/icons/Close';
import Typography from '@alltrails/shared/denali/components/Typography';
import PortalModal from 'components/shared/PortalModal/PortalModal';
import useIsMobile from '@alltrails/shared/hooks/useIsMobile';
import StarRating from '@alltrails/shared/denali/components/StarRating';
import { FormattedMessage, defineMessages, useIntl } from '@alltrails/shared/react-intl';
import Texture from '@alltrails/shared/denali/components/Texture';
import InstallPromptLocation from '@alltrails/analytics/enums/InstallPromptLocation';
import InstallPromptType from '@alltrails/analytics/enums/InstallPromptType';
import logInstallPromptViewed from '@alltrails/analytics/events/logInstallPromptViewed';
import { useEffect } from 'react';
import InstallPromptSource from '@alltrails/analytics/enums/InstallPromptSource';
import logInstallPromptDismissed from '@alltrails/analytics/events/logInstallPromptDismissed';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import classNames from 'classnames';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import Logo from '@alltrails/shared/denali/components/Logo';
import * as styles from './styles/styles.module.scss';
import appIconSrc from '../../../../assets/images/icons/app_icon.png';
import phoneSrcAvif from './phone.avif';
import phoneSrcWebp from './phone.webp';
import phoneSrc from './phone.png';
import phoneShortPng from './experimentImages/phone-short.png';
import phoneShortAvif from './experimentImages/phone-short.avif';
import phoneShortWebp from './experimentImages/phone-short.webp';
import useDeeplink from '../hooks/useDeeplink';
import { deepLinkRedirect } from '../utils';
import lifeStyleSrc from './experimentImages/lifestyle.jpg';
import lifeStyleAvif from './experimentImages/lifestyle.avif';
import lifeStyleWebp from './experimentImages/lifestyle.webp';
import useInstallAOTYExperiment from '../hooks/useInstallAOTYExperiment';

type BranchInterstitialProps = {
  promptLocation: InstallPromptLocation;
  pageId?: number;
  isOpen?: boolean;
  onRequestClose?: () => void;
  displayTrigger?: CarouselDisplayTrigger;
};

const br = () => <br />;

// Design link: https://www.figma.com/file/I38YmC36f1JlMJS4pjkqS5/App-of-the-Year?type=design&node-id=1213-39039&mode=design&t=VFO4CHX18FDb3Y4R-0

const ControlVariant = ({ phoneAltText }: { phoneAltText: string }) => (
  <>
    <div className={styles.imageContainer}>
      <Texture variant="leaves" imageSize="full" className={styles.texture} />
      <picture>
        <source srcSet={phoneSrcAvif} type="image/avif" />
        <source srcSet={phoneSrcWebp} type="image/webp" />
        <img src={phoneSrc} alt={phoneAltText} className={styles.phone} />
      </picture>
    </div>
    <div className={styles.contentContainer}>
      <Typography variant="heading500" className={styles.header}>
        <FormattedMessage defaultMessage="AllTrails is better in the app" />
      </Typography>
      <div className={styles.divider} />
    </div>
  </>
);

const AppScreenVariant = ({ phoneAltText, aotyAltText, aotyEmblemSrc }: { phoneAltText: string; aotyAltText: string; aotyEmblemSrc: string }) => (
  <div className={classNames(styles.variantContainer, styles.appScreenVariant)}>
    <div className={styles.variantHeaderContainer}>
      <Typography variant="heading600" className={styles.variantHeader}>
        <FormattedMessage defaultMessage="AllTrails is <br></br> better in the app" values={{ br }} />
      </Typography>
      <img src={aotyEmblemSrc} alt={aotyAltText} className={styles.aotyEmblem} />
    </div>
    <div className={styles.variantPhoneContainer}>
      <picture>
        <source srcSet={phoneShortAvif} type="image/avif" />
        <source srcSet={phoneShortWebp} type="image/webp" />
        <img src={phoneShortPng} alt={phoneAltText} className={styles.variantPhone} />
      </picture>
    </div>
  </div>
);

const LifeStyleVariant = ({ bgAltText, aotyAltText, aotyEmblemSrc }: { bgAltText: string; aotyAltText: string; aotyEmblemSrc: string }) => (
  <div className={styles.variantContainer}>
    <picture>
      <source srcSet={lifeStyleAvif} type="image/avif" />
      <source srcSet={lifeStyleWebp} type="image/webp" />
      <img src={lifeStyleSrc} alt={bgAltText} className={styles.variantBg} />
    </picture>
    <div className={styles.variantHeaderContainer}>
      <Typography variant="heading600" className={styles.variantHeader}>
        <FormattedMessage defaultMessage="AllTrails is <br></br> better in the app" values={{ br }} />
      </Typography>
      <img src={aotyEmblemSrc} alt={aotyAltText} className={styles.aotyEmblem} />
    </div>
  </div>
);

const AppleVideoVariant = ({ aotyTextSrc, aotyAltText }: { aotyTextSrc: string; aotyAltText: string }) => (
  <div className={classNames(styles.variantContainer, styles.appleVideoVariant)}>
    <video autoPlay loop muted playsInline className={styles.variantBg}>
      <source src="https://cdn-assets.alltrails.com/assets/videos/aoty-square-video.mp4" type="video/mp4" />
    </video>
    <img src={aotyTextSrc} alt={aotyAltText} className={styles.aotyText} />
    <Logo color="light" size="sm" variant="logomark" />
  </div>
);

const BranchInterstitial = ({ promptLocation, pageId, isOpen = false, displayTrigger, onRequestClose }: BranchInterstitialProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const aotyEmblemSrc = require(`../../../../assets/images/aoty/aoty-${languageRegionCode}.svg`);
  const aotyTextSrc = require(`../../../../assets/images/aoty-text/aoty-${languageRegionCode}.svg`);

  const isMobile = useIsMobile();
  const intl = useIntl();
  const aotyExperiment = useInstallAOTYExperiment();
  const deeplink = useDeeplink('trail', pageId, aotyExperiment);
  const ALT_TEXT = defineMessages({
    PHONE_ALT_Text: {
      defaultMessage: 'Navigation feature from the AllTrails app'
    },
    AOTY_ALT_TEXT: {
      defaultMessage: 'iPhone App of the Year'
    },
    BG_ALT_TEXT: {
      defaultMessage: 'Adult and child holding hands on a hiking trail'
    }
  });

  const {
    formattedDefaultMessages: { PHONE_ALT_Text, AOTY_ALT_TEXT, BG_ALT_TEXT }
  } = useFormatMessage(ALT_TEXT);

  useEffect(() => {
    if (isOpen && isMobile) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: InstallPromptType.Interstitial,
        install_prompt_source: InstallPromptSource.Native,
        signup_modal_trigger: displayTrigger
      });
    }
  }, [displayTrigger, isOpen, promptLocation, isMobile]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: InstallPromptType.Interstitial,
      install_prompt_source: InstallPromptSource.Native
    });
    onRequestClose();
  };

  return (
    <PortalModal isOpen={isOpen && isMobile}>
      <div className={styles.container}>
        {(!aotyExperiment || aotyExperiment.value === 'control') && <ControlVariant phoneAltText={PHONE_ALT_Text} />}
        {aotyExperiment?.value === 'app_screen' && (
          <AppScreenVariant phoneAltText={PHONE_ALT_Text} aotyAltText={AOTY_ALT_TEXT} aotyEmblemSrc={aotyEmblemSrc} />
        )}
        {aotyExperiment?.value === 'lifestyle' && (
          <LifeStyleVariant bgAltText={BG_ALT_TEXT} aotyAltText={AOTY_ALT_TEXT} aotyEmblemSrc={aotyEmblemSrc} />
        )}
        {aotyExperiment?.value === 'apple_video' && <AppleVideoVariant aotyAltText={AOTY_ALT_TEXT} aotyTextSrc={aotyTextSrc} />}

        <IconButton
          className={classNames(styles.close, { [styles.white]: aotyExperiment && aotyExperiment.value !== 'control' })}
          icon={{ Component: Close }}
          onClick={handleCloseClick}
          size="md"
          title={intl.formatMessage({ defaultMessage: 'Close' })}
          testId="close-button"
          variant="flat"
        />
        <div className={styles.contentContainer}>
          <div className={styles.logoAndRatingContainer}>
            <img src={appIconSrc} alt="" />
            <div className={styles.nameAndRatingContainer}>
              <Typography variant="heading200">AllTrails</Typography>
              <div className={styles.ratingContainer}>
                <StarRating rating={5} variant="complex" />
                <Typography variant="text100" component="span">
                  (4.9)
                </Typography>
              </div>
            </div>
          </div>
          <Button
            text={intl.formatMessage({ defaultMessage: 'Continue in app' })}
            onClick={() => deepLinkRedirect(deeplink, InstallPromptType.Interstitial, promptLocation)}
            testId="banner-button"
            variant="primary"
            size="md"
            allowWrap
            width="fullOnMobile"
          />
        </div>
      </div>
    </PortalModal>
  );
};

export default BranchInterstitial;
